import { useEffect, useMemo, useState, type ReactNode } from 'react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { getYandexMapKeys, hasYandexMapKeySource } from '@noor/api';
import { YmapsContext, type YmapsBindings, type YmapsContextValue } from '@noor/maps';

/**
 * Loads the Yandex Maps v3 (ymaps3) SDK once via a CDN script, reactifies it,
 * and exposes the bindings through `YmapsContext`. Ported from cpanel's
 * `YMapsProvider` (Redux → React context; the SDK handle is infra/server-ish
 * state, NOT Zustand UI state). Singleton-guarded so React StrictMode's double
 * effect doesn't inject the script twice. When the backend cannot
 * provide a key, the provider is disabled and the map degrades to a placeholder.
 */

/**
 * Per-service HTTP API keys handed to `setApikeys`. Each is a distinct Yandex
 * product with its own key and quota — see `@noor/config` env for which is
 * which.
 */
interface ServiceKeys {
  /** Places / Organization Search — backs `ymaps3.search()`. */
  search: string;
  /** Suggest API — address autocomplete suggestions. */
  suggest: string;
}

let loadPromise: Promise<YmapsBindings> | null = null;

async function loadYmaps(
  apiKey: string,
  serviceKeys: ServiceKeys,
): Promise<YmapsBindings> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<YmapsBindings>((resolve, reject) => {
    if (window.ymaps3) {
      void initBindings(serviceKeys).then(resolve).catch(reject);
      return;
    }
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `https://api-maps.yandex.ru/v3/?apikey=${apiKey}&lang=ru_RU`;
    script.onload = () => {
      void initBindings(serviceKeys).then(resolve).catch(reject);
    };
    script.onerror = () => reject(new Error('Failed to load Yandex Maps SDK'));
    document.body.appendChild(script);
  });
  return loadPromise;
}

async function initBindings({ search, suggest }: ServiceKeys): Promise<YmapsBindings> {
  const ymaps = window.ymaps3;
  if (!ymaps) throw new Error('ymaps3 not present after load');
  await ymaps.ready;
  // Each HTTP service the SDK talks to is its own Yandex product with its own
  // key: `search` is Places / Organization Search (search-maps.yandex.ru/v1/),
  // `suggest` is the Suggest API. Without these the SDK signs those calls with
  // the JS API key and the backends answer 403. Left unset, that fallback
  // stays (the map works; address search doesn't), so both are optional like
  // the JS key — set only the ones we actually have.
  const apikeys: { search?: string; suggest?: string } = {};
  if (search) apikeys.search = search;
  if (suggest) apikeys.suggest = suggest;
  if (search || suggest) {
    ymaps.getDefaultConfig().setApikeys(apikeys);
  }
  ymaps.import.registerCdn('https://cdn.jsdelivr.net/npm/{package}', [
    '@yandex/ymaps3-default-ui-theme@0.0',
  ]);
  const reactifyModule = (await ymaps.import('@yandex/ymaps3-reactify')) as {
    reactify: {
      bindTo: (r: typeof React, rd: typeof ReactDOM) => YmapsBindings['reactify'];
    };
  };
  const reactify = reactifyModule.reactify.bindTo(React, ReactDOM);
  return { ymaps3: ymaps, reactify };
}

export function YmapsProvider({ children }: { children: ReactNode }) {
  const [isDisabled, setIsDisabled] = useState(!hasYandexMapKeySource());
  const [bindings, setBindings] = useState<YmapsBindings | null>(null);

  useEffect(() => {
    if (isDisabled) return;
    let active = true;
    getYandexMapKeys()
      .then((keys) => {
        if (!keys.yandex) {
          if (active) setIsDisabled(true);
          return null;
        }
        return loadYmaps(keys.yandex, {
          search: keys.search,
          suggest: keys.suggest,
        });
      })
      .then((b) => {
        if (active && b) setBindings(b);
      })
      .catch(() => {
        // Loading failed — leave bindings null; the map shows its placeholder.
      });
    return () => {
      active = false;
    };
  }, [isDisabled]);

  const value = useMemo<YmapsContextValue>(
    () => ({ bindings, isReady: bindings !== null, isDisabled }),
    [bindings, isDisabled],
  );

  return <YmapsContext.Provider value={value}>{children}</YmapsContext.Provider>;
}
